import clsx from 'clsx';
import { AlertTriangle, History, Loader2, X } from 'lucide-react';
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { timeBucketLabel } from '@studio/shared';
import { localized, tx, txn } from '../i18n';

/* -------------------------------------------------------------- primitives */

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return <Loader2 size={size} className={clsx('animate-spin', className)} />;
}

export function Chip({
  children,
  tone = 'default',
  className,
  title,
  onClick,
}: {
  children: ReactNode;
  tone?: 'default' | 'accent' | 'brand' | 'teal' | 'camel';
  className?: string;
  title?: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      title={title}
      onClick={onClick}
      className={clsx(
        'chip',
        tone === 'accent' && 'chip-accent',
        tone === 'brand' && 'chip-brand',
        tone === 'teal' && 'chip-teal',
        tone === 'camel' && 'chip-camel',
        onClick && 'cursor-pointer hover:brightness-95',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/** Difficulty as camels — the notation the sheets themselves use. */
export function Camels({ n, title }: { n: number | null | undefined; title?: string }) {
  if (!n) return null;
  return (
    <span className="chip chip-camel" title={title ?? txn(n, '{n} camel (as annotated)', '{n} camels (as annotated)')}>
      {/* the corpus annotates 1–5; capping at 4 silently drew a 5 as a 4 */}
      {'🐪'.repeat(Math.max(1, Math.min(n, 5)))}
    </span>
  );
}

/** Our own 1–5 rating, drawn as a compact meter so it reads at a glance. */
export function DifficultyMeter({ value, label = true }: { value: number; label?: boolean }) {
  if (!value) return null;
  const tone =
    value <= 2 ? 'var(--color-teal-500)' : value === 3 ? 'var(--color-camel-500)' : 'var(--color-danger-500)';
  return (
    <span className="inline-flex items-center gap-1" title={tx('Rated difficulty {value} of 5', { value })}>
      <span className="flex gap-[2px]">
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className="h-3 w-[3px] rounded-full"
            style={{ background: i <= value ? tone : 'var(--line-strong)' }}
          />
        ))}
      </span>
      {label && <span className="text-xs text-[var(--ink-faint)]">{value}/5</span>}
    </span>
  );
}

export function TimeBadge({ minutes }: { minutes: number | null | undefined }) {
  if (!minutes) return null;
  return (
    <span className="chip" title={tx('Estimated {minutes} minutes for a student', { minutes })}>
      ⏱ {minutes < 60 ? tx('{minutes} min', { minutes }) : `${Math.round(minutes / 6) / 10} h`}
    </span>
  );
}

export function TimeBucket({ minutes }: { minutes: number }) {
  return <span className="chip">{timeBucketLabel(minutes)}</span>;
}

/**
 * Open defects recorded for an exercise.  Colour follows the worst severity
 * present, because that is what decides whether the sheet can be handed out.
 */
export function DefectBadge({
  defects,
}: {
  defects: { total: number; high: number; medium: number; low: number } | undefined;
}) {
  if (!defects?.total) return null;
  const worst = defects.high ? 'high' : defects.medium ? 'medium' : 'low';
  const parts = [
    defects.high && tx('{n} high', { n: defects.high }),
    defects.medium && tx('{n} medium', { n: defects.medium }),
    defects.low && tx('{n} low', { n: defects.low }),
  ].filter(Boolean);
  return (
    <span
      className={clsx(
        'chip gap-1',
        worst === 'high' && 'chip-danger',
        worst === 'medium' && 'chip-camel',
      )}
      title={tx('Reviewed issues: {list}', { list: parts.join(', ') })}
    >
      <AlertTriangle size={11} />
      {defects.total}
    </span>
  );
}

/** The programming language of the exercise's code, when it has any. */
export function CodeLangBadge({ language }: { language: string | undefined }) {
  if (!language || language === 'none') return null;
  return (
    <span className="chip" title={tx('Code is written in {language}', { language: language === 'sml' ? 'SML' : 'OCaml' })}>
      {language === 'sml' ? 'SML' : 'OCaml'}
    </span>
  );
}

/**
 * Which markup an exercise (or sheet) is written in: Typst or LaTeX.
 *
 * No markup means LaTeX, as everywhere: everything written before markup existed is.
 * Typst keeps the accent colour; LaTeX is a plain chip. `header` is the LaTeX header the
 * item is compiled with, when the caller knows it, and goes into the tooltip.
 */
export function MarkupBadge({ markup, header }: { markup: string | undefined; header?: string }) {
  if (markup === 'typst') {
    return (
      <span className="chip chip-accent" title={tx('Written in the Typst format')} data-testid="markup-badge" data-markup="typst">
        Typst
      </span>
    );
  }
  return (
    <span
      className="chip"
      title={header ? tx('Written in the LaTeX format — compiled with the LaTeX header {header}', { header }) : tx('Written in the LaTeX format')}
      data-testid="markup-badge"
      data-markup="latex"
    >
      LaTeX
    </span>
  );
}

/** German or English — the language the statement is written in. */
export function ProseLangBadge({ language }: { language: string | undefined }) {
  if (!language) return null;
  return (
    <span className="chip" title={language === 'de' ? tx('German statement') : tx('English statement')}>
      {language === 'de' ? 'DE' : 'EN'}
    </span>
  );
}

const SERIES_LABEL: Record<string, string> = localized('series', {
  en: {
    tutorial: 'tutorial',
    exercise: 'exercise',
    'mock-exam': 'mock exam',
    'exam-prep': 'exam prep',
    cards: 'cards',
  },
  de: {
    tutorial: 'Tutorium',
    exercise: 'Übung',
    'mock-exam': 'Probeklausur',
    'exam-prep': 'Klausurvorb.',
    cards: 'Karten',
  },
});

/**
 * Which series the exercise came from.  Every year but 2020 runs tutorial sheets
 * and marked exercise sheets side by side, and a tutor picking exercises needs to
 * know which is which.
 */
export function SeriesBadge({ kind }: { kind: string | undefined }) {
  const label = kind ? SERIES_LABEL[kind] : undefined;
  if (!label) return null;
  return (
    <span
      className={clsx('chip', kind === 'exercise' && 'chip-accent')}
      title={
        kind === 'exercise'
          ? tx('From an exercise sheet — the series handed in for marking')
          : tx('From a {label} sheet', { label })
      }
    >
      {label}
    </span>
  );
}

/** "3 versions" — this exercise was reused and revised in later years. */
export function VersionsBadge({ size, years }: { size: number | undefined; years?: string[] }) {
  if (!size || size < 2) return null;
  const span = years && years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : null;
  return (
    <span
      className="chip gap-1"
      title={
        span
          ? tx('{size} versions, {span} — the newest is the one listed', { size, span })
          : tx('{size} versions across years — newest shown', { size })
      }
    >
      <History size={11} />
      {span ?? size}
    </span>
  );
}

export function Button({
  variant = 'outline',
  size,
  loading,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'accent' | 'ghost' | 'outline' | 'danger';
  size?: 'sm';
  loading?: boolean;
}) {
  return (
    <button
      className={clsx('btn focus-ring', `btn-${variant}`, size === 'sm' && 'btn-sm', className)}
      {...rest}
      /*
       * After the spread, not before. `{...rest}` carries `disabled` — usually
       * `undefined` — and re-applied it over the computed value, so a `loading` button
       * that was not also explicitly disabled stayed clickable and could be submitted
       * again while its first request was still in flight.
       */
      disabled={rest.disabled || loading}
    >
      {loading && <Spinner size={14} />}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      aria-label={label}
      title={label}
      className={clsx('btn btn-icon btn-ghost focus-ring', className)}
      {...rest}
    >
      {children}
    </button>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon && <div className="text-[var(--ink-faint)]">{icon}</div>}
      {/* h2: it often sits right under the page's h1, and h3 there skipped a level (axe "heading-order") */}
      <h2 className="text-base font-semibold">{title}</h2>
      {hint && <p className="max-w-md text-sm text-[var(--ink-soft)]">{hint}</p>}
      {action}
    </div>
  );
}

/* -------------------------------------------------------------------- modal */

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /*
   * `onClose` is read through a ref, and the effect below depends on `open` alone.
   *
   * Every caller passes an inline arrow, so depending on `onClose` re-ran the effect on
   * every render of the parent: its cleanup put focus back on the opener and the re-run
   * moved it to the first focusable element — the header's Close button. Typing one
   * character into "New sheet" or Sheet settings re-rendered the parent, focus jumped to
   * Close, the next key went nowhere, and Enter closed the dialog.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEscapeLayer(open, () => onCloseRef.current(), { modal: true });
  useFocusTrap(ref, open);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[8vh] backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className={clsx('card w-full', wide ? 'max-w-3xl' : 'max-w-lg')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className="flex items-center justify-between gap-4 border-b border-[var(--line)] px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <IconButton label={tx('Close')} onClick={onClose}>
            <X size={16} />
          </IconButton>
        </header>
        <div className="px-4 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-[var(--line)] px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- escape stack */

interface EscapeLayer {
  close: () => void;
  ignoreInFields: boolean;
  /** a dialog that owns the keyboard: global shortcuts are off while it is open */
  modal: boolean;
}

/** Whether a modal layer — a dialog, the palette, the picks tray — is open right now. */
export const modalOpen = (): boolean => escapeLayers.some((l) => l.modal);
const escapeLayers: EscapeLayer[] = [];

const isEditable = (el: EventTarget | null) =>
  el instanceof HTMLElement &&
  (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT');

if (typeof document !== 'undefined') {
  /*
   * One listener for every dismissable layer, and only the topmost one hears Escape.
   *
   * Each layer used to listen on the document for itself, so one Escape closed all of
   * them at once: the full-screen PDF *and* the exercise panel under it, a dialog *and*
   * the panel that opened it — and pressing Escape inside the panel's tag editor threw
   * the panel and its unsaved tags away.
   */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    const top = escapeLayers[escapeLayers.length - 1];
    if (!top) return;
    if (top.ignoreInFields && isEditable(e.target)) return;
    e.preventDefault();
    top.close();
  });
}

/**
 * Register a layer that Escape dismisses while `active`. Layers opened later sit on top.
 * `ignoreInFields` is for panels a user types into, where Escape belongs to the field.
 */
export function useEscapeLayer(
  active: boolean,
  close: () => void,
  options: { ignoreInFields?: boolean; modal?: boolean } = {},
) {
  const closeRef = useRef(close);
  closeRef.current = close;
  const ignoreInFields = options.ignoreInFields ?? false;
  const modal = options.modal ?? false;
  useEffect(() => {
    if (!active) return;
    const layer: EscapeLayer = { close: () => closeRef.current(), ignoreInFields, modal };
    escapeLayers.push(layer);
    return () => {
      const index = escapeLayers.lastIndexOf(layer);
      if (index >= 0) escapeLayers.splice(index, 1);
    };
  }, [active, ignoreInFields, modal]);
}

/**
 * Keyboard focus for anything that covers the page: moved in when it opens (unless a
 * field inside already took it with `autoFocus`), kept in with Tab and Shift Tab, and
 * returned to whatever opened it when it closes.
 *
 * The opener is captured while rendering the opening: by the time an effect runs, a
 * field inside may already have the focus, and that is not where it should return.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, open: boolean) {
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current && typeof document !== 'undefined') {
    openerRef.current = document.activeElement as HTMLElement | null;
  }
  wasOpen.current = open;
  useEffect(() => {
    if (!open) return;
    const opener = openerRef.current;
    /*
     * Exactly the stops Tab makes, or the wrap misfires and focus falls out to the page
     * behind. The Details drawer's "Advanced" section did both ways: its <summary> was not
     * in the list, and the fields folded inside it were — Chrome lays out a closed
     * <details>' content (content-visibility: hidden), so `offsetParent` does not rule
     * them out. The last "item" was a field Tab never reaches, and Tab from the summary
     * left the drawer; Shift Tab from the first control skipped the summary.
     */
    const inClosedDetails = (el: HTMLElement) => {
      for (let d = el.closest('details'); d; d = d.parentElement?.closest('details') ?? null) {
        if (!d.open && !(el.tagName === 'SUMMARY' && el.parentElement === d)) return true;
      }
      return false;
    };
    const focusable = () =>
      Array.from(
        ref.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
            'details > summary, [contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null && !inClosedDetails(el));
    if (!ref.current?.contains(document.activeElement)) (focusable()[0] ?? ref.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !ref.current?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !ref.current?.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // back to the opener, if it is still on the page (a floating button may be gone)
      if (opener?.isConnected) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

/* ------------------------------------------------------------------ helpers */

export function SkeletonCard() {
  return (
    <div className="card overflow-hidden">
      <div className="skeleton h-44 w-full" />
      <div className="space-y-2 p-3">
        <div className="skeleton h-3 w-1/3 rounded" />
        <div className="skeleton h-3 w-2/3 rounded" />
      </div>
    </div>
  );
}

export function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="text-sm font-semibold tracking-tight">{children}</h2>
      {right}
    </div>
  );
}
